import { useI18n } from '@upweo/ui';
import { useQuery } from '@tanstack/react-query';

import * as api from '../lib/api.js';
import { usePartner } from '../lib/partner-context.js';

export const ThemesPage = (): React.JSX.Element => {
  const { t } = useI18n();
  const { activeAccount } = usePartner();

  const themesQuery = useQuery({
    queryKey: ['themes', activeAccount?.id],
    queryFn: () => api.listThemes(),
    enabled: Boolean(activeAccount),
  });

  const themes = themesQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('partner.themes')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Storefront themes you publish · {themes.length}
        </p>
      </div>

      {themesQuery.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}

      {!themesQuery.isPending && themes.length === 0 && (
        <div className="rounded-lg border bg-card p-10 text-center">
          <p className="text-sm text-muted-foreground">{t('partner.noThemes')}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Theme uploads arrive with the storefront engine, which defines the runtime contract a
            theme is written against.
          </p>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {themes.map((t) => (
          <div key={t.id} className="rounded-lg border bg-card p-5">
            <div className="flex items-start justify-between">
              <h2 className="font-medium">{t.name}</h2>
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                v{t.latestVersion}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{t.description ?? '—'}</p>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span>{t.priceAmount ? api.formatMoney(t.priceAmount) : 'Free'}</span>
              <span className="text-muted-foreground">{t.installCount} installs</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
